import { useEffect, useState, useRef } from "react";
import { api, errMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = ["transcript", "ID proof", "CE certificate", "license", "headshot", "other"];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("transcript");
  const inputRef = useRef();

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/files");
      setDocs(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      reload();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/files/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const download = async (d) => {
    try {
      const resp = await api.get(`/files/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div className="animate-fade-up" data-testid="documents-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Documents</div>
        <h1 className="font-heading text-3xl font-bold text-primary">Your documents</h1>
        <p className="text-sm text-slate-500 mt-1">Transcripts, ID proofs, CE certificates and supporting documentation. Maximum 10 MB per file.</p>
      </div>

      <Card className="p-5 border-slate-200 bg-slate-50 mb-6">
        <div className="grid sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-4">
            <Label className="mb-1.5 block text-xs">Document type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doctype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-6">
            <Label className="mb-1.5 block text-xs">File (PDF · JPG · PNG)</Label>
            <Input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={upload} data-testid="upload-file-input" />
          </div>
          <div className="sm:col-span-2">
            <Button disabled className="w-full" variant="outline">
              <Upload className="h-4 w-4 mr-1.5" />
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                <th className="p-4">File</th>
                <th className="p-4">Type</th>
                <th className="p-4">Application</th>
                <th className="p-4">Uploaded</th>
                <th className="p-4">Size</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && docs.length === 0 && (
                <tr><td colSpan={6} className="p-12 text-center">
                  <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">No documents yet</div>
                </td></tr>
              )}
              {docs.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="p-4 font-medium text-primary truncate max-w-[260px]">{d.original_filename}</td>
                  <td className="p-4"><Badge variant="secondary" className="bg-slate-100">{d.doc_type}</Badge></td>
                  <td className="p-4 text-xs font-mono-data text-slate-500">{d.application_id ? d.application_id.slice(0, 8) + "…" : "—"}</td>
                  <td className="p-4 text-xs text-slate-500">{new Date(d.created_at).toLocaleString()}</td>
                  <td className="p-4 text-xs text-slate-500">{(d.size / 1024).toFixed(1)} KB</td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => download(d)} className="mr-2" data-testid={`download-${d.id}`}>Download</Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(d.id)} data-testid={`delete-${d.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
